import React, { useState, useEffect } from 'react';
import { View } from 'react-native';
import Input from '@/components/General/inputs/Input';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import SelectInput from '@/components/General/inputs/SelectInput';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import SheetModal from '@/components/General/SheetModal';

/**
 * Options editor for a section: title, description, and an optional
 * show-conditionally rule (fieldId + eq + value). `fields` is the flat list of
 * fields available to reference in the condition (typically all fields in the
 * form that come before this section).
 */
const SectionOptionsSheet = ({ isOpen, onClose, section, fields = [], onChange }) => {
    const [local, setLocal] = useState(null);

    useEffect(() => {
        if (section) {
            setLocal(JSON.parse(JSON.stringify(section)));
        } else {
            setLocal(null);
        }
    }, [section]);

    const update = (patch) => {
        if (!local) return;
        const next = { ...local, ...patch };
        setLocal(next);
        onChange(next);
    };

    const updateCondition = (patch) => {
        if (!local) return;
        const condition = { ...(local.condition || { fieldId: null, operator: 'eq', value: '' }), ...patch };
        const next = { ...local, condition };
        setLocal(next);
        onChange(next);
    };

    if (!local) return null;

    const hasCondition = !!local.condition;
    const conditionField = fields.find((f) => f.fieldId === local.condition?.fieldId);
    const conditionValueOptions = (conditionField?.config?.options || []).map((o) =>
        typeof o === 'object' ? o : { label: String(o), value: String(o) }
    );

    // Renders the "Equals" input with a control that matches the chosen field's
    // type so the user picks a value in the same shape the field stores.
    const renderEqualsInput = () => {
        if (!conditionField) return null;

        switch (conditionField.type) {
            case 'checkbox':
                return (
                    <SelectInput
                        title="Equals"
                        placeholder="Select a value"
                        value={local.condition?.value ?? null}
                        onUpdate={(val) => updateCondition({ value: val })}
                        options={[
                            { label: 'Is On', value: true },
                            { label: 'Is Off', value: false },
                        ]}
                    />
                );
            case 'dropdown':
            case 'checkboxGroup':
                return (
                    <SelectInput
                        title="Equals"
                        placeholder="Select a value"
                        value={local.condition?.value ?? null}
                        onUpdate={(val) => updateCondition({ value: val })}
                        options={conditionValueOptions}
                    />
                );
            case 'number':
                return (
                    <Input
                        title="Equals"
                        value={local.condition?.value == null ? '' : String(local.condition.value)}
                        onUpdate={(text) => {
                            if (text === '' || text == null) {
                                updateCondition({ value: null });
                                return;
                            }
                            const num = Number(text);
                            updateCondition({ value: Number.isNaN(num) ? null : num });
                        }}
                        type="decimal"
                        placeholder="The value to match"
                    />
                );
            case 'date':
            case 'datetime':
            case 'time':
                return (
                    <DatePickerInput
                        title="Equals"
                        value={local.condition?.value ? new Date(local.condition.value) : null}
                        onUpdate={(date) => updateCondition({ value: date ? date.toISOString() : null })}
                        mode={conditionField.type === 'time' ? 'time' : conditionField.type === 'datetime' ? 'datetime' : 'date'}
                    />
                );
            default:
                return (
                    <Input
                        title="Equals"
                        value={local.condition?.value || ''}
                        onUpdate={(val) => updateCondition({ value: val })}
                        placeholder="The value to match"
                    />
                );
        }
    };

    return (
        <SheetModal
            title="Section Options"
            isOpen={isOpen}
            onCancel={onClose}
            helpText="Configure this section's title and optional display condition."
        >
            <Input
                title="Section Title"
                value={local.title}
                onUpdate={(val) => update({ title: val })}
                placeholder="e.g. Contact Info"
            />

            <Input
                title="Description"
                value={local.description || ''}
                onUpdate={(val) => update({ description: val })}
                placeholder="Shown as subtext under the title"
                multiline
            />

            <ToggleRow
                label="Allow Add More"
                description="Let respondents click an 'Add Another' button to fill out this section more than once."
                value={!!local.allowAddMore}
                disabled={local.fields?.some((field) => field.type === 'payment')}
                onValueChange={(val) => update({ allowAddMore: val })}
            />

            {!!local.allowAddMore && (
                <Input
                    title="Add Button Text"
                    value={local.addMoreLabel || ''}
                    onUpdate={(val) => update({ addMoreLabel: val })}
                    placeholder="Add Another"
                />
            )}

            <ToggleRow
                label="Show Conditionally"
                description="Only show this section when another field matches a value."
                value={hasCondition}
                onValueChange={(val) => {
                    if (val) {
                        updateCondition({ fieldId: fields[0]?.fieldId || null, operator: 'eq', value: '' });
                    } else {
                        update({ condition: undefined });
                    }
                }}
            />

            {hasCondition && (
                <View style={styles.conditionBlock}>
                    <View style={styles.conditionRow}>
                        <View style={styles.conditionField}>
                            <SelectInput
                                title="When Field"
                                placeholder="Select a field"
                                value={local.condition?.fieldId || null}
                                onUpdate={(val) => updateCondition({ fieldId: val, value: null })}
                                options={fields.map((f) => ({ label: f.label || f.fieldId, value: f.fieldId }))}
                            />
                        </View>
                        <View style={styles.conditionField}>
                            {conditionField && renderEqualsInput()}
                        </View>
                    </View>
                </View>
            )}
        </SheetModal>
    );
};

const styles = {
    conditionBlock: { marginTop: 8 },
    conditionRow: { flexDirection: 'row', gap: 8 },
    conditionField: { flex: 1 },
};

export default SectionOptionsSheet;
